/**
 * WeaveIn.ai Platform - Member Workflow Progress Component
 * Visual workflow progress display with step execution capabilities
 */

import React, { useState } from 'react';
import {
  CheckCircle,
  Circle,
  Clock,
  Play,
  SkipForward,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Phone,
  Mail,
  FileText,
  Loader2,
  Plus,
  UserPlus,
  XCircle,
  Upload,
  FolderOpen,
  ClipboardCheck,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { SignaturePad, SignatureResult } from '@/components/signatures/SignaturePad';
import { SignatureViewer } from '@/components/signatures/SignatureViewer';
import { config } from '@/src/config';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

import useWorkflowInstance from '@/hooks/useWorkflowInstance';
import {
  StepInstanceDetail,
  StepInstanceStatus,
  StepType,
  StepExecuteAction,
  WorkflowInstanceStatus,
} from '@/services/api/workflows';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';

// Navigation context types for workflow step actions
export interface FormNavigationContext {
  formTemplateId?: number;
  formType?: string;
  workflowStepId: number;
  workflowStepInstanceId: number;
  workflowInstanceId: number;
  stepName: string;
}

export interface CommunicationNavigationContext {
  channel: 'sms' | 'email' | 'phone';
  templateId?: number;
  workflowStepId: number;
  workflowStepInstanceId: number;
  workflowInstanceId: number;
  stepName: string;
}

export interface DocumentNavigationContext {
  requiredDocuments?: string[];
  workflowStepId: number;
  workflowStepInstanceId: number;
  workflowInstanceId: number;
  stepName: string;
}

interface MemberWorkflowProgressProps {
  workflowInstanceId: number;
  enrollmentId?: number;
  onStepExecute?: (stepId: number, action: StepExecuteAction) => void;
  /** @deprecated Use onNavigateToFormWithContext instead */
  onNavigateToForm?: (formType: string) => void;
  /** Enhanced form navigation with full workflow context */
  onNavigateToFormWithContext?: (context: FormNavigationContext) => void;
  /** @deprecated Use onNavigateToCommunicationWithContext instead */
  onNavigateToCommunication?: (channel: 'sms' | 'email' | 'phone') => void;
  /** Enhanced communication navigation with full workflow context */
  onNavigateToCommunicationWithContext?: (context: CommunicationNavigationContext) => void;
  /** Navigate to documents tab with context */
  onNavigateToDocuments?: (context: DocumentNavigationContext) => void;
  onAddActivity?: () => void;
}

export const MemberWorkflowProgress: React.FC<MemberWorkflowProgressProps> = ({
  workflowInstanceId,
  enrollmentId,
  onStepExecute,
  onNavigateToForm,
  onNavigateToFormWithContext,
  onNavigateToCommunication,
  onNavigateToCommunicationWithContext,
  onNavigateToDocuments,
  onAddActivity,
}) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const { instance, loading, error, executeStep, refetch } = useWorkflowInstance(workflowInstanceId);

  const [expandedStepId, setExpandedStepId] = useState<number | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedStep, setSelectedStep] = useState<StepInstanceDetail | null>(null);
  const [selectedAction, setSelectedAction] = useState<StepExecuteAction | null>(null);
  const [actionNotes, setActionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Signature dialog state
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [signingStep, setSigningStep] = useState<StepInstanceDetail | null>(null);

  const handleSignatureSubmit = async (result: SignatureResult) => {
    if (!signingStep) return;
    try {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      // 1. Create signing request
      const reqRes = await fetch(`${config.weaveinApi.baseUrl}/api/signatures/requests`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': cboId || '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: `Approval: ${signingStep.step_name}`,
          workflow_step_instance_id: signingStep.id,
          signer_type: 'internal',
          signer_role: signingStep.step_config?.approver_role || 'supervisor',
          expires_in_hours: 168,
        }),
      });

      if (!reqRes.ok) throw new Error('Failed to create signing request');
      const sigReq = await reqRes.json();

      // 2. Submit signature
      const sigRes = await fetch(`${config.weaveinApi.baseUrl}/api/signatures/requests/${sigReq.id}/sign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': cboId || '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(result),
      });

      if (!sigRes.ok) throw new Error('Failed to submit signature');

      setSignatureDialogOpen(false);
      setSigningStep(null);
      alert('Approved and signed successfully');
      refetch();
    } catch (e: any) {
      alert(`Signature error: ${e.message}`);
    }
  };

  // Get step type icon
  const getStepTypeIcon = (type: StepType, config?: Record<string, any>) => {
    // Check step_config for sub-type (e.g., task with task_type=document_upload)
    if (config?.task_type === 'document_upload') return <Upload className="h-4 w-4" />;
    switch (type) {
      case 'task':
        return <FileText className="h-4 w-4" />;
      case 'notification':
        return <AlertCircle className="h-4 w-4" />;
      case 'communication':
        return <MessageSquare className="h-4 w-4" />;
      case 'wait_for_response':
        return <Clock className="h-4 w-4" />;
      case 'approval':
        return <CheckCircle className="h-4 w-4" />;
      case 'form':
        return <FileText className="h-4 w-4" />;
      case 'assessment':
        return <ClipboardCheck className="h-4 w-4" />;
      case 'document':
        return <Upload className="h-4 w-4" />;
      case 'billing':
        return <FileText className="h-4 w-4" />;
      default:
        return <Circle className="h-4 w-4" />;
    }
  };

  // Get status styling
  const getStatusStyles = (status: StepInstanceStatus) => {
    switch (status) {
      case 'completed':
        return {
          bgColor: 'bg-green-100 dark:bg-green-900/30',
          borderColor: 'border-green-500',
          iconColor: 'text-green-600 dark:text-green-400',
          lineColor: 'bg-green-500',
        };
      case 'in_progress':
        return {
          bgColor: 'bg-blue-100 dark:bg-blue-900/30',
          borderColor: 'border-blue-500',
          iconColor: 'text-blue-600 dark:text-blue-400',
          lineColor: 'bg-blue-500',
        };
      case 'skipped':
        return {
          bgColor: 'bg-gray-100 dark:bg-gray-800',
          borderColor: 'border-gray-400',
          iconColor: 'text-gray-500',
          lineColor: 'bg-gray-400',
        };
      case 'failed':
        return {
          bgColor: 'bg-red-100 dark:bg-red-900/30',
          borderColor: 'border-red-500',
          iconColor: 'text-red-600 dark:text-red-400',
          lineColor: 'bg-red-500',
        };
      default: // pending
        return {
          bgColor: 'bg-gray-50 dark:bg-gray-900',
          borderColor: 'border-gray-300 dark:border-gray-600',
          iconColor: 'text-gray-400',
          lineColor: 'bg-gray-300 dark:bg-gray-600',
        };
    }
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status: StepInstanceStatus) => {
    switch (status) {
      case 'completed':
        return 'default';
      case 'in_progress':
        return 'default';
      case 'skipped':
        return 'secondary';
      case 'failed':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  // Get workflow status badge variant
  const getWorkflowStatusVariant = (status: WorkflowInstanceStatus) => {
    switch (status) {
      case 'completed':
        return 'default';
      case 'running':
        return 'default';
      case 'failed':
        return 'destructive';
      case 'cancelled':
        return 'secondary';
      case 'paused':
        return 'outline';
      default:
        return 'outline';
    }
  };

  // Handle step action
  const handleStepAction = async () => {
    if (!selectedStep || !selectedAction) return;

    setActionLoading(true);
    try {
      // Use the step instance id (selectedStep.id) for the API call
      const response = await executeStep(selectedStep.id, selectedAction, actionNotes);
      if (response?.success) {
        onStepExecute?.(selectedStep.id, selectedAction);
        setActionDialogOpen(false);
        setActionNotes('');
        setSelectedStep(null);
        setSelectedAction(null);
      }
    } catch (err) {
      console.error('Error executing step:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Open action dialog
  const openActionDialog = (step: StepInstanceDetail, action: StepExecuteAction) => {
    setSelectedStep(step);
    setSelectedAction(action);
    setActionDialogOpen(true);
  };

  // Render step actions based on status
  const renderStepActions = (step: StepInstanceDetail) => {
    const isCurrentStep = instance?.current_step_id === step.workflow_step_id;
    const canExecute = step.status === 'pending' || step.status === 'in_progress';

    if (!canExecute) return null;

    return (
      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
        {step.status === 'pending' && (
          <Button size="sm" onClick={() => openActionDialog(step, 'start')}>
            <Play className="h-3 w-3 mr-1" />
            Start
          </Button>
        )}

        {step.status === 'in_progress' && (
          <>
            <Button size="sm" onClick={() => openActionDialog(step, 'complete')}>
              <CheckCircle className="h-3 w-3 mr-1" />
              Complete
            </Button>
            {!step.is_critical && (
              <Button size="sm" variant="outline" onClick={() => openActionDialog(step, 'skip')}>
                <SkipForward className="h-3 w-3 mr-1" />
                Skip
              </Button>
            )}
          </>
        )}

        {/* Step type specific actions */}
        {step.step_type === 'communication' && step.status === 'in_progress' && (
          <>
            {step.step_config?.channel === 'sms' && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  if (onNavigateToCommunicationWithContext) {
                    onNavigateToCommunicationWithContext({
                      channel: 'sms',
                      templateId: step.step_config?.communication_template_id,
                      workflowStepId: step.workflow_step_id,
                      workflowStepInstanceId: step.id,
                      workflowInstanceId: workflowInstanceId,
                      stepName: step.step_name,
                    });
                  } else {
                    onNavigateToCommunication?.('sms');
                  }
                }}
              >
                <MessageSquare className="h-3 w-3 mr-1" />
                Send SMS
              </Button>
            )}
            {step.step_config?.channel === 'email' && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  if (onNavigateToCommunicationWithContext) {
                    onNavigateToCommunicationWithContext({
                      channel: 'email',
                      templateId: step.step_config?.communication_template_id,
                      workflowStepId: step.workflow_step_id,
                      workflowStepInstanceId: step.id,
                      workflowInstanceId: workflowInstanceId,
                      stepName: step.step_name,
                    });
                  } else {
                    onNavigateToCommunication?.('email');
                  }
                }}
              >
                <Mail className="h-3 w-3 mr-1" />
                Send Email
              </Button>
            )}
            {(step.step_config?.channel === 'phone' || step.step_config?.channel === 'voice') && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  if (onNavigateToCommunicationWithContext) {
                    onNavigateToCommunicationWithContext({
                      channel: 'phone',
                      templateId: step.step_config?.communication_template_id,
                      workflowStepId: step.workflow_step_id,
                      workflowStepInstanceId: step.id,
                      workflowInstanceId: workflowInstanceId,
                      stepName: step.step_name,
                    });
                  } else {
                    onNavigateToCommunication?.('phone');
                  }
                }}
              >
                <Phone className="h-3 w-3 mr-1" />
                Make Call
              </Button>
            )}
          </>
        )}

        {/* Show Open Form button for form/assessment steps or any step with form_template_id */}
        {(step.step_type === 'form' || step.step_type === 'assessment' || step.step_config?.form_template_id) && ['pending', 'in_progress'].includes(step.status) && (
          <Button
            size="sm"
            variant="secondary"
            className="bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100"
            onClick={() => {
              if (onNavigateToFormWithContext) {
                onNavigateToFormWithContext({
                  formTemplateId: step.step_config?.form_template_id || step.form_template_id,
                  formType: step.step_config?.form_type || step.step_type || 'custom',
                  workflowStepId: step.workflow_step_id,
                  workflowStepInstanceId: step.id,
                  workflowInstanceId: workflowInstanceId,
                  stepName: step.step_name,
                });
              } else {
                onNavigateToForm?.(step.step_config?.form_type || 'custom');
              }
            }}
          >
            <FileText className="h-3 w-3 mr-1" />
            {step.step_type === 'assessment' ? 'Open Assessment' : 'Open Form'}
          </Button>
        )}

        {(step.step_type === 'document' || step.step_config?.task_type === 'document_upload') && ['pending', 'in_progress'].includes(step.status) && onNavigateToDocuments && (
          <div className="flex flex-col gap-1.5">
            {/* Show required document types */}
            {step.step_config?.required_documents && step.step_config.required_documents.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {step.step_config.required_documents.map((doc: string) => (
                  <Badge key={doc} variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                    <FolderOpen className="h-2.5 w-2.5 mr-1" />
                    {doc}
                  </Badge>
                ))}
              </div>
            )}
            <Button
              size="sm"
              variant="secondary"
              className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
              onClick={() => {
                onNavigateToDocuments({
                  requiredDocuments: step.step_config?.required_documents,
                  workflowStepId: step.workflow_step_id,
                  workflowStepInstanceId: step.id,
                  workflowInstanceId: workflowInstanceId,
                  stepName: step.step_name,
                });
              }}
            >
              <Upload className="h-3 w-3 mr-1" />
              Upload Document
            </Button>
          </div>
        )}

        {/* Approval step: Approve & Sign / Reject buttons */}
        {step.step_type === 'approval' && ['pending', 'in_progress'].includes(step.status) && (
          <div className="flex flex-col gap-2 w-full">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <CheckCircle className="w-3 h-3" />
              Requires {step.step_config?.approver_role?.replace('_', ' ') || 'supervisor'} approval
              {step.step_config?.requires_signature && ' with signature'}
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                className="bg-v2accent hover:bg-v2accent/90 text-white"
                onClick={() => {
                  if (step.step_config?.requires_signature) {
                    setSigningStep(step);
                    setSignatureDialogOpen(true);
                  } else {
                    openActionDialog(step, 'complete');
                  }
                }}
              >
                <CheckCircle className="h-3 w-3 mr-1" />
                {step.step_config?.requires_signature ? 'Approve & Sign' : 'Approve'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => openActionDialog(step, 'fail')}
              >
                <XCircle className="h-3 w-3 mr-1" />
                Reject
              </Button>
            </div>
          </div>
        )}

        {onAddActivity && (
          <Button size="sm" variant="ghost" onClick={onAddActivity}>
            <Plus className="h-3 w-3 mr-1" />
            Add Activity
          </Button>
        )}
      </div>
    );
  };

  // Get step status icon
  const renderStepStatusIcon = (status: StepInstanceStatus) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />;
      case 'in_progress':
        return <Play className="h-5 w-5 text-blue-600 dark:text-blue-400" />;
      case 'skipped':
        return <SkipForward className="h-5 w-5 text-gray-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />;
      default:
        return <Circle className="h-5 w-5 text-gray-400" />;
    }
  };

  if (loading && !instance) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm text-muted-foreground">Loading workflow...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!instance) {
    return null;
  }

  return (
    <Card className="mb-4">
      {/* Workflow Header */}
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <CardTitle className="text-lg">{instance.template_name || 'Workflow'}</CardTitle>
            {instance.template_description && (
              <p className="text-sm text-muted-foreground mt-1">{instance.template_description}</p>
            )}
          </div>
          <Badge variant={getWorkflowStatusVariant(instance.status)} className="self-start sm:self-center">
            {instance.status}
          </Badge>
        </div>

        {/* Progress Bar */}
        <div className="flex items-center gap-3 mt-3">
          <Progress value={instance.progress_percentage} className="flex-1" />
          <span className="text-sm font-medium whitespace-nowrap">
            {instance.progress_percentage}%
          </span>
        </div>
      </CardHeader>

      {/* Steps Flow */}
      <CardContent className="pt-0">
        <div className="space-y-0">
          {instance.steps.map((step, index) => {
            const styles = getStatusStyles(step.status);
            const isExpanded = expandedStepId === step.id;
            const isLast = index === instance.steps.length - 1;
            const isCurrentStep = instance.current_step_id === step.workflow_step_id;

            return (
              <div key={step.id} className="relative">
                {/* Connecting Line */}
                {!isLast && (
                  <div
                    className={`absolute left-[19px] top-[40px] w-0.5 h-[calc(100%-32px)] ${styles.lineColor}`}
                  />
                )}

                {/* Step Card */}
                <div className="flex gap-3">
                  {/* Step Circle */}
                  <div
                    className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center ${styles.bgColor} ${styles.borderColor}`}
                  >
                    {renderStepStatusIcon(step.status)}
                  </div>

                  {/* Step Content */}
                  <div
                    className={`flex-1 mb-4 p-3 rounded-lg border ${
                      isCurrentStep ? 'border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-950/20' : 'border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <div
                      className="flex items-start justify-between cursor-pointer"
                      onClick={() => setExpandedStepId(isExpanded ? null : step.id)}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                            Step {step.step_order}
                          </span>
                          <span className="font-medium">{step.step_name}</span>
                          {step.is_critical && (
                            <Badge variant="destructive" className="text-[10px] px-1 py-0 h-4">
                              Required
                            </Badge>
                          )}
                          {isCurrentStep && (
                            <Badge variant="default" className="text-[10px] px-1 py-0 h-4 bg-blue-600">
                              Current
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                          {step.step_type && getStepTypeIcon(step.step_type, step.step_config)}
                          <span className="capitalize">{step.step_type ? step.step_type.replace('_', ' ') : 'Task'}</span>
                          <Badge variant={getStatusBadgeVariant(step.status)} className="ml-1 text-[10px]">
                            {step.status.replace('_', ' ')}
                          </Badge>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-dashed">
                        {/* Step Configuration Info */}
                        {step.step_config?.description && (
                          <p className="text-sm text-muted-foreground mb-2">
                            {step.step_config.description}
                          </p>
                        )}

                        {/* Timestamps */}
                        <div className="flex flex-wrap gap-4 text-xs text-muted-foreground mb-2">
                          {step.started_at && (
                            <span>
                              Started: {fmt.dateTime(step.started_at)}
                            </span>
                          )}
                          {step.completed_at && (
                            <span>
                              Completed: {fmt.dateTime(step.completed_at)}
                            </span>
                          )}
                          {step.assigned_to_name && (
                            <span className="flex items-center gap-1">
                              <UserPlus className="h-3 w-3" />
                              {step.assigned_to_name}
                            </span>
                          )}
                        </div>

                        {/* Notes */}
                        {step.completion_notes && (
                          <div className="text-sm bg-muted/50 p-2 rounded mt-2">
                            <span className="font-medium">Notes:</span> {step.completion_notes}
                          </div>
                        )}

                        {/* Signature record for completed approval steps */}
                        {(step.step_type === 'approval' || step.completion_notes?.includes('Signed by')) && step.status === 'completed' && (
                          <SignatureViewer stepInstanceId={step.id} compact />
                        )}

                        {/* Actions */}
                        {renderStepActions(step)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedAction === 'start' && 'Start Step'}
              {selectedAction === 'complete' && 'Complete Step'}
              {selectedAction === 'skip' && 'Skip Step'}
              {selectedAction === 'fail' && 'Mark Step as Failed'}
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            {selectedStep && (
              <div className="mb-4 p-3 bg-muted rounded-lg">
                <p className="font-medium">{selectedStep.step_name}</p>
                <p className="text-sm text-muted-foreground capitalize">
                  {selectedStep.step_type.replace('_', ' ')}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                placeholder="Add notes about this action..."
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleStepAction} disabled={actionLoading}>
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {selectedAction === 'start' && 'Start Step'}
              {selectedAction === 'complete' && 'Complete Step'}
              {selectedAction === 'skip' && 'Skip Step'}
              {selectedAction === 'fail' && 'Mark as Failed'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signature Dialog for Approval Steps */}
      <Dialog open={signatureDialogOpen} onOpenChange={setSignatureDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Approve & Sign</DialogTitle>
          </DialogHeader>
          <SignaturePad
            signerName=""
            title={`Sign: ${signingStep?.step_name || 'Approval'}`}
            message="By signing below, you approve this workflow step and confirm your authorization."
            onSign={handleSignatureSubmit}
            onCancel={() => { setSignatureDialogOpen(false); setSigningStep(null); }}
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default MemberWorkflowProgress;
